// Page "Informations société" — permet de configurer les données de la société
// affichées sur les devis et factures (nom, adresse, logo, coordonnées bancaires…).

import { ChangeEvent, FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { companySettingsApi } from "../api/companySettings";
import { CurrencySelect } from "../components/CurrencySelect";
import { useAuthStore } from "../stores/auth";
import type { CompanySettings, CompanySettingsUpdate } from "../api/companySettings";
import { ApiError } from "../api/client";
import { AppShell } from "../components/AppShell";
import { FormField } from "../components/FormField";

// ---- Empty defaults --------------------------------------------------------

function emptySettings(): CompanySettingsUpdate {
  return {
    company_name: null,
    email: null,
    phone: null,
    website: null,
    address_line1: null,
    address_line2: null,
    postal_code: null,
    city: null,
    country: null,
    siret: null,
    vat_number: null,
    default_vat_rate: 0,
    bank_name: null,
    iban: null,
    bic: null,
    legal_footer: null,
  };
}

// ---- Page ------------------------------------------------------------------

export function CompanySettingsPage() {
  const { t } = useTranslation();

  const [settings, setSettings] = useState<CompanySettings | null>(null);
  const [form, setForm] = useState<CompanySettingsUpdate>(emptySettings());
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const currentTenant = useAuthStore(s => s.currentTenant);
  const [currencySaving, setCurrencySaving] = useState(false);
  const [currencySuccess, setCurrencySuccess] = useState(false);
  const [currencyError, setCurrencyError] = useState<string | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<string>(currentTenant?.currency ?? "EUR");

  const [logoUploading, setLogoUploading] = useState(false);
  const [logoSuccess, setLogoSuccess] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // ---- Load ----------------------------------------------------------------

  useEffect(() => {
    setLoadError(null);
    companySettingsApi
      .get()
      .then((data) => {
        setSettings(data);
        setForm({
          company_name: data.company_name,
          email: data.email,
          phone: data.phone,
          website: data.website,
          address_line1: data.address_line1,
          address_line2: data.address_line2,
          postal_code: data.postal_code,
          city: data.city,
          country: data.country,
          siret: data.siret,
          vat_number: data.vat_number,
          default_vat_rate: data.default_vat_rate ?? 0,
          bank_name: data.bank_name,
          iban: data.iban,
          bic: data.bic,
          legal_footer: data.legal_footer,
        });
      })
      .catch((err) => {
        setLoadError(err instanceof Error ? err.message : "Erreur de chargement");
      });
  }, []);

  // ---- Save ----------------------------------------------------------------

  async function handleCurrencyUpdate(currency: string) {
    setCurrencySaving(true);
    setCurrencySuccess(false);
    setCurrencyError(null);
    try {
      const { api } = await import("../api/client");
      await api.patch("/tenants/me/currency", { currency });
      await useAuthStore.getState().refreshMe();
      setCurrencySuccess(true);
      setTimeout(() => setCurrencySuccess(false), 3000);
    } catch (err) {
      setCurrencyError(err instanceof Error ? err.message : "Erreur lors de la mise à jour de la devise");
    } finally {
      setCurrencySaving(false);
    }
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);
    try {
      const updated = await companySettingsApi.update(form);
      setSettings(updated);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(
        err instanceof ApiError ? err.detail : t("companySettings.saveError")
      );
    } finally {
      setSaving(false);
    }
  }

  // ---- Logo upload ---------------------------------------------------------

  async function onLogoChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    setLogoSuccess(false);
    setLogoError(null);
    try {
      const updated = await companySettingsApi.uploadLogo(file);
      setSettings(updated);
      setLogoSuccess(true);
      setTimeout(() => setLogoSuccess(false), 3000);
    } catch (err) {
      setLogoError(
        err instanceof ApiError ? err.detail : t("companySettings.logoError")
      );
    } finally {
      setLogoUploading(false);
      // Reset the file input so the same file can be re-uploaded later
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  // ---- Helpers -------------------------------------------------------------

  function field(key: keyof CompanySettingsUpdate) {
    return {
      value: (form[key] as string | null) ?? "",
      onChange: (
        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      ) =>
        setForm((prev) => ({
          ...prev,
          [key]: e.target.value || null,
        })),
    };
  }

  // ---- Render --------------------------------------------------------------

  if (loadError) {
    return (
      <AppShell>
        <p role="alert" className="text-red-600 text-sm p-6">
          {loadError}
        </p>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">
          {t("companySettings.title")}
        </h1>
        <p className="text-sm text-slate-500">{t("companySettings.subtitle")}</p>
      </header>

      <form onSubmit={onSubmit} className="space-y-8 max-w-2xl">

        {/* ---- Identité -------------------------------------------------- */}
        <section>
          <h2 className="text-base font-semibold text-slate-800 mb-4">
            {t("companySettings.identity")}
          </h2>
          <div className="space-y-4">
            <FormField label={t("companySettings.companyName")} htmlFor="company_name">
              <input id="company_name" className="input" {...field("company_name")} />
            </FormField>
            <FormField label={t("companySettings.email")} htmlFor="cs_email">
              <input id="cs_email" type="email" className="input" {...field("email")} />
            </FormField>
            <FormField label={t("companySettings.phone")} htmlFor="cs_phone">
              <input id="cs_phone" className="input" {...field("phone")} />
            </FormField>
            <FormField label={t("companySettings.website")} htmlFor="cs_website">
              <input id="cs_website" type="url" className="input" {...field("website")} />
            </FormField>
          </div>
        </section>

        {/* ---- Adresse --------------------------------------------------- */}
        <section>
          <h2 className="text-base font-semibold text-slate-800 mb-4">
            {t("companySettings.address")}
          </h2>
          <div className="space-y-4">
            <FormField label={t("companySettings.addressLine1")} htmlFor="address_line1">
              <input id="address_line1" className="input" {...field("address_line1")} />
            </FormField>
            <FormField label={t("companySettings.addressLine2")} htmlFor="address_line2">
              <input id="address_line2" className="input" {...field("address_line2")} />
            </FormField>
            <div className="grid grid-cols-2 gap-4">
              <FormField label={t("companySettings.postalCode")} htmlFor="postal_code">
                <input id="postal_code" className="input" {...field("postal_code")} />
              </FormField>
              <FormField label={t("companySettings.city")} htmlFor="city">
                <input id="city" className="input" {...field("city")} />
              </FormField>
            </div>
            <FormField label={t("companySettings.country")} htmlFor="country">
              <input id="country" className="input" {...field("country")} />
            </FormField>
          </div>
        </section>

        {/* ---- Légal ----------------------------------------------------- */}
        <section>
          <h2 className="text-base font-semibold text-slate-800 mb-4">
            {t("companySettings.legal")}
          </h2>
          <div className="space-y-4">
            <FormField label={t("companySettings.siret")} htmlFor="siret">
              <input id="siret" className="input" {...field("siret")} />
            </FormField>
            <FormField label={t("companySettings.vatNumber")} htmlFor="vat_number">
              <input id="vat_number" className="input" {...field("vat_number")} />
            </FormField>

            <FormField label={t("companySettings.defaultVatRate", "Taux TVA par défaut (%)")} htmlFor="default_vat_rate">
              <div className="flex items-center gap-2">
                <input id="default_vat_rate" type="number" min="0" max="100" step="0.01"
                  className="input w-32" {...field("default_vat_rate")} />
                <span className="text-sm text-slate-500">%</span>
                <span className="text-xs text-slate-400 ml-2">{t("companySettings.vatRateHint", "Ex : 5 pour les Émirats, 20 pour la France")}</span>
              </div>
            </FormField>
          </div>
        </section>

        {/* ---- Logo ------------------------------------------------------ */}
        <section>
          <h2 className="text-base font-semibold text-slate-800 mb-4">
            {t("companySettings.logo")}
          </h2>
          <div className="space-y-3">
            {settings?.logo_url && (
              <div className="mb-2">
                <img
                  src={settings.logo_url}
                  alt="Logo société"
                  className="h-16 object-contain rounded border border-slate-200"
                />
              </div>
            )}
            <p className="text-xs text-slate-500">{t("companySettings.logoHint")}</p>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              className="hidden"
              onChange={onLogoChange}
            />
            <button
              type="button"
              className="btn-primary"
              disabled={logoUploading}
              onClick={() => fileRef.current?.click()}
            >
              {logoUploading ? "…" : t("companySettings.changeLogo")}
            </button>
            {logoSuccess && (
              <p className="text-sm text-green-600">{t("companySettings.logoUploaded")}</p>
            )}
            {logoError && (
              <p role="alert" className="text-sm text-red-600">{logoError}</p>
            )}
          </div>
        </section>

        {/* ---- Coordonnées bancaires -------------------------------------- */}
        <section>
          <h2 className="text-base font-semibold text-slate-800 mb-4">
            {t("companySettings.bank")}
          </h2>
          <div className="space-y-4">
            <FormField label={t("companySettings.bankName")} htmlFor="bank_name">
              <input id="bank_name" className="input" {...field("bank_name")} />
            </FormField>
            <FormField label={t("companySettings.iban")} htmlFor="iban">
              <input id="iban" className="input font-mono" {...field("iban")} />
            </FormField>
            <FormField label={t("companySettings.bic")} htmlFor="bic">
              <input id="bic" className="input font-mono" {...field("bic")} />
            </FormField>
          </div>
        </section>

        {/* ---- Mentions légales ------------------------------------------ */}
        <section>
          <h2 className="text-base font-semibold text-slate-800 mb-4">
            {t("companySettings.legalFooter")}
          </h2>
          <div className="space-y-2">
            <p className="text-xs text-slate-500">{t("companySettings.legalFooterHint")}</p>
            <textarea
              rows={4}
              className="input w-full"
              value={(form.legal_footer as string | null) ?? ""}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  legal_footer: e.target.value || null,
                }))
              }
            />
          </div>
        </section>

        {/* ---- Devise par défaut ---------------------------------------- */}
        <section>
          <h2 className="text-base font-semibold text-slate-800 mb-4">
            {t("companySettings.currency", "Devise par défaut")}
          </h2>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">{t("companySettings.currencyHint", "Appliquée aux nouvelles factures par défaut")}</p>
            <div className="flex items-center gap-3">
              <CurrencySelect
                id="tenant_currency"
                value={selectedCurrency}
                onChange={v => setSelectedCurrency(v)}
                className="input w-48"
              />
              <button
                type="button"
                disabled={currencySaving}
                onClick={() => handleCurrencyUpdate(selectedCurrency)}
                className="btn-primary"
              >
                {currencySaving ? t("common.saving") : t("common.save")}
              </button>
            </div>
            {currencySuccess && <p className="text-sm text-green-600">{t("common.saved", "Sauvegardé")}</p>}
            {currencyError && <p role="alert" className="text-sm text-red-600">{currencyError}</p>}
          </div>
        </section>

        {/* ---- Feedback + Submit ----------------------------------------- */}
        {saveError && (
          <p role="alert" className="text-sm text-red-600">{saveError}</p>
        )}
        {saveSuccess && (
          <p className="text-sm text-green-600">{t("companySettings.saved")}</p>
        )}

        <div className="flex justify-end pt-2">
          <button
            type="submit"
            disabled={saving || settings === null}
            className="btn-primary"
          >
            {saving ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </form>
    </AppShell>
  );
}
